import React, { useState, useEffect } from 'react';
import { X, Key, ExternalLink, Check, MessageSquareQuote } from 'lucide-react';
import { useSubscription } from '../contexts/SubscriptionContext';
import { getVoiceProfile, isVoiceEnabled } from '../services/voiceProfile.js';
import VoiceProfileModal from './VoiceProfileModal';

const AISettingsModal = ({ isOpen, onClose, onSave, showNotification }) => {
    const { isPremium, debugSetPremium } = useSubscription();
    const [voiceOpen, setVoiceOpen] = useState(false);
    const [voiceSavedLen, setVoiceSavedLen] = useState(0);
    const [keys, setKeys] = useState({
        gemini: '',
        openai: '',
        anthropic: '',
        groq: '',
        deepseek: '',
        rapidapi: ''
    });

    useEffect(() => {
        setKeys({
            gemini: localStorage.getItem('gemini_api_key') || '',
            openai: localStorage.getItem('openai_api_key') || '',
            anthropic: localStorage.getItem('anthropic_api_key') || '',
            groq: localStorage.getItem('groq_api_key') || '',
            deepseek: localStorage.getItem('deepseek_api_key') || '',
            rapidapi: localStorage.getItem('rapidapi_key') || ''
        });
        setVoiceSavedLen((getVoiceProfile() || '').length);
    }, [isOpen, voiceOpen]);

    const handleSave = () => {
        localStorage.setItem('gemini_api_key', keys.gemini);
        localStorage.setItem('openai_api_key', keys.openai);
        localStorage.setItem('anthropic_api_key', keys.anthropic);
        localStorage.setItem('groq_api_key', keys.groq);
        localStorage.setItem('deepseek_api_key', keys.deepseek);
        localStorage.setItem('rapidapi_key', keys.rapidapi);

        onSave(keys);
        onClose();
    };

    const handleChange = (provider, value) => {
        setKeys(prev => ({ ...prev, [provider]: value }));
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
            <div className="bg-white rounded-xl shadow-2xl w-full max-w-md overflow-hidden max-h-[90vh] overflow-y-auto">
                <div className="bg-slate-50 px-6 py-4 border-b border-slate-200 flex justify-between items-center sticky top-0 z-10">
                    <h3 className="font-bold text-slate-800 flex items-center gap-2">
                        <Key size={18} className="text-purple-600" />
                        AI Settings
                    </h3>
                    <button onClick={onClose} className="text-slate-400 hover:text-slate-600">
                        <X size={20} />
                    </button>
                </div>

                <div className="p-6 space-y-6">
                    <div className="bg-blue-50 text-blue-800 text-xs p-3 rounded-lg">
                        <strong>Note:</strong> If you get stuck on a "Verify you are human" check when clicking the links below, please try opening the link in a <strong>new Incognito window</strong>.
                    </div>
                    <p className="text-sm text-slate-600">
                        Configure multiple AI providers for redundancy. Tried in order: Gemini → Groq → DeepSeek → OpenAI → Anthropic.
                    </p>

                    {/* Gemini Section */}
                    <div className="space-y-2">
                        <label className="block text-xs font-bold text-slate-700 uppercase flex justify-between items-center">
                            Google Gemini API Key (Primary)
                            <span className="text-xs font-normal text-emerald-600 bg-emerald-50 px-2 py-0.5 rounded-full">Recommended</span>
                        </label>
                        <input
                            type="password"
                            value={keys.gemini}
                            onChange={(e) => handleChange('gemini', e.target.value)}
                            placeholder="AIzaSy..."
                            className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-purple-500 outline-none font-mono text-sm"
                        />
                        <a
                            href="https://aistudio.google.com/app/apikey"
                            target="_blank"
                            rel="noreferrer"
                            className="inline-flex items-center gap-1 text-xs text-purple-600 hover:underline"
                        >
                            Get free Gemini Key <ExternalLink size={10} />
                        </a>
                    </div>

                    {/* Groq Section */}
                    <div className="space-y-2 pt-4 border-t border-slate-100">
                        <label className="block text-xs font-bold text-slate-700 uppercase flex justify-between items-center">
                            Groq API Key (Fast)
                            <span className="text-xs font-normal text-orange-600 bg-orange-50 px-2 py-0.5 rounded-full">Sub-second</span>
                        </label>
                        <input
                            type="password"
                            value={keys.groq}
                            onChange={(e) => handleChange('groq', e.target.value)}
                            placeholder="gsk_..."
                            className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-orange-500 outline-none font-mono text-sm"
                        />
                        <a
                            href="https://console.groq.com/keys"
                            target="_blank"
                            rel="noreferrer"
                            className="inline-flex items-center gap-1 text-xs text-orange-600 hover:underline"
                        >
                            Get free Groq Key <ExternalLink size={10} />
                        </a>
                    </div>

                    {/* DeepSeek Section */}
                    <div className="space-y-2 pt-4 border-t border-slate-100">
                        <label className="block text-xs font-bold text-slate-700 uppercase flex justify-between items-center">
                            DeepSeek API Key (Cheap)
                            <span className="text-xs font-normal text-cyan-600 bg-cyan-50 px-2 py-0.5 rounded-full">~10× cheaper</span>
                        </label>
                        <input
                            type="password"
                            value={keys.deepseek}
                            onChange={(e) => handleChange('deepseek', e.target.value)}
                            placeholder="sk-..."
                            className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-cyan-500 outline-none font-mono text-sm"
                        />
                        <a
                            href="https://platform.deepseek.com/api_keys"
                            target="_blank"
                            rel="noreferrer"
                            className="inline-flex items-center gap-1 text-xs text-cyan-600 hover:underline"
                        >
                            Get DeepSeek Key <ExternalLink size={10} />
                        </a>
                    </div>

                    {/* OpenAI Section */}
                    <div className="space-y-2 pt-4 border-t border-slate-100">
                        <label className="block text-xs font-bold text-slate-700 uppercase">OpenAI API Key (Backup)</label>
                        <input
                            type="password"
                            value={keys.openai}
                            onChange={(e) => handleChange('openai', e.target.value)}
                            placeholder="sk-..."
                            className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-green-500 outline-none font-mono text-sm"
                        />
                        <div className="flex justify-between items-center">
                            <a
                                href="https://platform.openai.com/api-keys"
                                target="_blank"
                                rel="noreferrer"
                                className="inline-flex items-center gap-1 text-xs text-green-600 hover:underline"
                            >
                                Get OpenAI Key <ExternalLink size={10} />
                            </a>
                            <span className="text-[10px] text-slate-400">Requires Login</span>
                        </div>
                    </div>

                    {/* Anthropic Section */}
                    <div className="space-y-2 pt-4 border-t border-slate-100">
                        <label className="block text-xs font-bold text-slate-700 uppercase">Anthropic API Key (Backup)</label>
                        <input
                            type="password"
                            value={keys.anthropic}
                            onChange={(e) => handleChange('anthropic', e.target.value)}
                            placeholder="sk-ant-..."
                            className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-amber-500 outline-none font-mono text-sm"
                        />
                        <div className="flex justify-between items-center">
                            <a
                                href="https://console.anthropic.com/settings/keys"
                                target="_blank"
                                rel="noreferrer"
                                className="inline-flex items-center gap-1 text-xs text-amber-600 hover:underline"
                            >
                                Get Anthropic Key <ExternalLink size={10} />
                            </a>
                            <span className="text-[10px] text-slate-400">Requires Login</span>
                        </div>
                    </div>

                    {/* RapidAPI Job Search Section */}
                    <div className="space-y-2 pt-4 border-t border-slate-100">
                        <label className="block text-xs font-bold text-slate-700 uppercase">RapidAPI Key (For Job Search)</label>
                        <input
                            type="password"
                            value={keys.rapidapi}
                            onChange={(e) => handleChange('rapidapi', e.target.value)}
                            placeholder="RapidAPI Key..."
                            className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none font-mono text-sm"
                        />
                        <div className="flex justify-between items-center">
                            <a
                                href="https://rapidapi.com/letscrape-6bRBa3QguO5/api/jsearch"
                                target="_blank"
                                rel="noreferrer"
                                className="inline-flex items-center gap-1 text-xs text-blue-600 hover:underline"
                            >
                                Get Free JSearch Key <ExternalLink size={10} />
                            </a>
                            <span className="text-[10px] text-slate-400">Requires Login</span>
                        </div>
                    </div>

                    {/* Voice DNA launcher */}
                    <button
                        onClick={() => setVoiceOpen(true)}
                        className="w-full flex items-center justify-between gap-3 pt-4 mt-2 border-t border-slate-100 hover:bg-slate-50 -mx-6 px-6 py-3 transition-colors"
                    >
                        <div className="flex items-center gap-2">
                            <MessageSquareQuote size={18} className="text-purple-600" />
                            <div className="text-left">
                                <div className="text-sm font-bold text-slate-800">Voice DNA</div>
                                <div className="text-xs text-slate-500">
                                    {voiceSavedLen > 0
                                        ? `${voiceSavedLen} chars saved · ${isVoiceEnabled() ? 'ACTIVE' : 'off'}`
                                        : 'Make AI sound like you'}
                                </div>
                            </div>
                        </div>
                        <span className="text-xs text-purple-600 font-medium">Configure →</span>
                    </button>

                    <button
                        onClick={handleSave}
                        className="w-full bg-slate-900 hover:bg-slate-800 text-white font-bold py-3 rounded-lg flex items-center justify-center gap-2 transition-colors mt-6"
                    >
                        <Check size={18} />
                        Save All Keys
                    </button>

                    {/* Developer Debug Tools */}
                    <div className="pt-4 mt-4 border-t border-slate-100">
                        <p className="text-xs font-bold text-slate-400 uppercase mb-2">Developer Tools</p>
                        <label className="flex items-center justify-between p-3 bg-slate-50 rounded-lg cursor-pointer hover:bg-slate-100 transition-colors">
                            <span className="text-sm font-medium text-slate-700">Force Premium Status</span>
                            <input
                                type="checkbox"
                                checked={isPremium}
                                onChange={(e) => debugSetPremium(e.target.checked)}
                                className="w-5 h-5 text-purple-600 rounded focus:ring-purple-500 border-gray-300"
                            />
                        </label>
                    </div>
                </div>
            </div>

            <VoiceProfileModal
                isOpen={voiceOpen}
                onClose={() => setVoiceOpen(false)}
                showNotification={showNotification}
            />
        </div>
    );
};

export default AISettingsModal;
